<template>
    <div class="support-modal">
        <div class="support-modal-window">
            <header>
                <h2 v-html="this.$i18n('account_profile_customer_support_message_title')" />
                <CloseButton @click="close" class="close" v-if="closable" />
            </header>
            <main>
                <div class="text" v-html="this.$i18n('account_profile_customer_support_message_description')"/>
                <div class="field" :class="{ focused: this.messageInputFocused }">
                    <textarea name="message" ref="message" rows="4"
                           @focus="messageInputFocused = true"
                           @blur="messageInputFocused = false"
                           v-model="userMessage"
                           :placeholder="messagePlaceholder" />
                </div>
                <div class="actions">
                    <v-button :i18n="{ id: 'account_profile_cancel_btn' }" @click.native="close" size="medium" />
                    <v-button :i18n="{ id: 'account_profile_customer_support_message_send_btn' }" :disabled="!messageIsValid" @click.native="sendMessage" :filled="true" size="medium" />
                </div>
            </main>
        </div>
    </div>
</template>

<script>
import CloseButton from 'Images/close-24x24.svg';
import VButton from "ViewComponent/v-button.vue";
import i18nUtils from "Util/i18n.js";

export default {
    name: 'support-modal',
    props: {
        closable: {
            type: Boolean,
            default: true
        }
    },
    components: {
        VButton,
        CloseButton,
    },
    data() {
        return {
            messageInputFocused: false,
            userMessage: ''
        }
    },
    computed: {
        messagePlaceholder() {
            return i18nUtils.prop('account_profile_customer_support_message_textbox_placeholder');
        },
        messageIsValid() {
            return this.userMessage.length >= 2;
        }
    },
    methods: {
        close() {
            if (this.closable) {
                this.$emit('close');
            }
        },
        keyDown(event) {
            if (this.closable && event && event.code === 'Escape') { // close on ESC key
                this.close()
            }
        },
        sendMessage() {
            if (this.messageIsValid) {
                this.$emit('send', this.userMessage.trim());
            }
        },
        focusInput() {
            this.messageInputFocused = true;
            this.$refs.message.focus();
        }
    },
    mounted() {
        document.addEventListener('keydown', this.keyDown);
        document.body.classList.add('modal-open');
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.keyDown);
        document.body.classList.remove('modal-open');
    }
};
</script>

<style lang="scss">
    @import "~Styles/colors";
    @import "~Styles/mixins";

    div.support-modal {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 1000;
        width: 100vw;
        height: 100vh;
        background-color: rgba(44, 49, 67, 0.8);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        > div.support-modal-window {
            position: relative;
            @include elevated;
            background-color: #fff;
            border-radius: 0.75rem;
            display: flex;
            flex-direction: column;
            max-height: calc(100% - 2rem);
            min-width: pxToRem(400);
            max-width: pxToRem(600);
            box-sizing: border-box;
            padding-bottom: 1.5rem;
            gap: 1rem;

            > header {
                padding: 1.5rem 1.5rem 0 1.5rem;
                display: flex;
                flex-direction: row;
                body[data-interface-language="ar"] & {
                    flex-direction: row-reverse;
                }
                > svg.close {
                    cursor: pointer;
                    transition: 100ms ease-in-out;
                    margin-left: auto;
                    body[data-interface-language="ar"] & {
                        margin-left: unset;
                        margin-right: auto;
                    }
                    &:hover {
                        transform: scale(1.2);
                    }
                }

                > h2 {
                    font-size: fontSize(20);
                    line-height: fontSize(24);
                    text-align: left;
                    margin-right: 1rem;
                    body[data-interface-language="ar"] & {
                        text-align: right;
                        margin-left: 1rem;
                        margin-right: unset;
                    }
                }
            }

            > main {
                padding: 0 1.5rem;
                display: flex;
                flex-direction: column;
                gap: 1rem;
                div.field {
                    display: flex;
                    flex-direction: row;
                    gap: 1rem;
                    padding: .5rem;
                    border: 2px solid $colorBackgroundDarker;
                    border-radius: .5rem;
                    &.focused {
                        border: 2px solid $colorBackgroundDark;
                        border-radius: .5rem;
                    }
                    > textarea {
                        flex: 1;
                        @include minimalistScrollbar(8px);
                    }
                    > svg.edit-icon {
                        margin-left: auto;
                        cursor: pointer;
                        transition: all 150ms;
                        &:hover {
                            transform: scale(1.2);
                        }
                    }
                }
                > div.actions {
                    margin-top: auto;
                    display: flex;
                    flex-direction: row;
                    justify-content: flex-end;
                    gap: 1rem;
                }
            }

        }
    }
</style>
